import mustache from "mustache";
import { serviceProvider } from "../../../../comun/provider/ServiceProvider";
import {
  getDescargaLitrosHa,
  getLitrosProducto,
  getMezclaLitros,
  getResumen,
  LoteMedido,
  ParametrosMedicion,
} from "../../../../modulos/aplicaciones/calculoAplicacion";
import { AplicacionRequest } from "../../../../modulos/aplicaciones/request/AplicacionRequest";
import { ClienteFincaLoteResponse } from "../../../../modulos/terceros/response/ClienteFincaLoteResponse";
import { fx_createLoteGrpcService } from "../../../../modulos/terceros/services/LoteGrpcService";
import { IFormField } from "../../../definitions/form/IFormField";
import { LotePickerModalRender } from "../../terceros/lote/modal/LotePickerModalRender";
import {
  AplicacionProductoLoteChart,
  fx_colorProducto,
  ProductoDosis,
} from "./AplicacionProductoLoteChart";
import rowTemplate from "./aplicacionLoteRowTemplate.html";
import template from "./aplicacionLotesFieldTemplate.html";

mustache.parse(template);
mustache.parse(rowTemplate);

/** Contador incremental de instancias, para no colisionar ids en el DOM. */
let LOTES_ID = 0;

/** Formatea con separador de miles y hasta `decimales` decimales, sin forzarlos:
 *  200 se lee "200" y 2,5 conserva su coma. */
const fx_num = (valor: number, decimales: number = 1): string =>
  valor.toLocaleString("es-CO", { maximumFractionDigits: decimales });

/**
 * Tabla de **lotes aplicados** de una aplicación manual, con sus KPI.
 *
 * Es la pieza que una aplicación por dron no necesita: el KML del vuelo ya dice
 * por dónde pasó y cuánto soltó, mientras que acá lo único que se sabe es "en el
 * lote 4 se echaron 3 canecas". De estas filas salen el área y el volumen de la
 * aplicación.
 *
 * El lote se elige con el **mismo picker con mapa** que usan Broca y Pesaje
 * (`LotePickerModalRender`), filtrando los ya agregados — un lote repetido
 * duplicaría su área y su mezcla en los totales, y el server lo rechaza.
 *
 * A la derecha, los KPI y el **gráfico de producto por lote**, que responde lo
 * que los totales no contestan: cuánta mezcla se aplicó no dice cuánto producto
 * se gastó ni dónde. Los dos se actualizan mientras se teclea.
 *
 * Los encabezados y los KPI **siguen al método**: en drench se anotan plantas y
 * la columna calculada es la mezcla en litros; en el resto se anotan recipientes
 * y la columna es la descarga en L/ha. Quien cambia el método o el recipiente
 * llama a {@link refresh}.
 */
export class AplicacionLotesField implements IFormField {
  /** Id de instancia, para los ids del DOM. */
  private readonly _fieldId: number = ++LOTES_ID;

  /**
   * @param _request        Request que se está editando.
   * @param _getParametros  Con qué se está midiendo (método, litros del
   *                        recipiente, ml por planta). Es una función y no un
   *                        valor porque cambia mientras se edita el formulario.
   */
  constructor(
    private readonly _request: AplicacionRequest,
    private readonly _getParametros: () => ParametrosMedicion,
  ) {
    this._chart = new AplicacionProductoLoteChart(`ap-lotes-chart-${this._fieldId}`);
  }

  getId(): string {
    return `ap-lotes-${this._fieldId}`;
  }

  render(): string {
    return mustache.render(template, { id: this._fieldId });
  }

  connect(): void {
    this._rows = document.getElementById(`ap-lotes-rows-${this._fieldId}`);
    this._kpis = document.getElementById(`ap-lotes-kpis-${this._fieldId}`);
    this._leyenda = document.getElementById(`ap-lotes-leyenda-${this._fieldId}`);
    this._error = document.getElementById(`ap-lotes-error-${this._fieldId}`);
    this._agregar = document.getElementById(`ap-lotes-add-${this._fieldId}`);
    this._encabezadoCantidad = document.getElementById(
      `ap-lotes-th-cantidad-${this._fieldId}`,
    );
    this._encabezadoCalculado = document.getElementById(
      `ap-lotes-th-calculado-${this._fieldId}`,
    );

    // Delegados: un listener por evento para todas las filas (sobreviven a los
    // re-render de la tabla).
    this._rows?.addEventListener("input", this._onCantidadInput);
    this._rows?.addEventListener("click", this._onRemoveClick);
    this._agregar?.addEventListener("click", this._onAgregarClick);

    this._chart.connect();
    void this._cargarLotesAsync();
    this.refresh();
  }

  dispose(): void {
    this._disposed = true;
    this._rows?.removeEventListener("input", this._onCantidadInput);
    this._rows?.removeEventListener("click", this._onRemoveClick);
    this._agregar?.removeEventListener("click", this._onAgregarClick);
    this._picker?.dispose();
    this._picker = null;
    this._chart.dispose();
  }

  /** Re-pinta filas, encabezados y KPI. Lo llama el formulario cuando cambia el
   *  método, el recipiente o la mezcla por planta: los tres cambian los números
   *  de la columna calculada sin que se toque una sola fila. */
  refresh(): void {
    this._refreshEncabezados();
    this._refreshRows();
    this._refreshKpis();
    this._refreshChart();
  }

  /** Hace falta al menos un lote, y cada uno con cantidad > 0. */
  getError(): string | null {
    if (this._request.lotes.length === 0) {
      return "Agregue al menos un lote aplicado";
    }
    const esDrench = this._getParametros().metodo === "drench";
    const incompleto = this._request.lotes.some((lote) =>
      esDrench ? (lote.plantas ?? 0) <= 0 : (lote.cantidad ?? 0) <= 0,
    );
    if (incompleto) {
      return esDrench
        ? "Ingrese las plantas tratadas en cada lote"
        : "Ingrese la cantidad aplicada en cada lote";
    }
    return null;
  }

  showError(): void {
    if (this._error) this._error.innerHTML = this.getError() ?? "";
  }

  focus(): void {
    const input = this._rows?.querySelector<HTMLInputElement>("[data-cantidad]");
    if (input) input.focus();
    else this._agregar?.focus();
  }

  disabled(): void {
    this._setDisabled(true);
  }

  enabled(): void {
    this._setDisabled(false);
  }

  private _rows: HTMLElement | null = null;
  private _kpis: HTMLElement | null = null;
  private _leyenda: HTMLElement | null = null;
  private _error: HTMLElement | null = null;
  private _agregar: HTMLElement | null = null;
  private _encabezadoCantidad: HTMLElement | null = null;
  private _encabezadoCalculado: HTMLElement | null = null;
  private _picker: LotePickerModalRender | null = null;
  private _chart: AplicacionProductoLoteChart;
  private _disposed = false;

  /** Lotes de la finca por id: de acá sale el **área**, que el request no trae
   *  y sin la cual no hay descarga por hectárea. */
  private _lotesFinca = new Map<string, ClienteFincaLoteResponse>();

  /** Carga los lotes de la finca. Sale del **mismo servicio cacheado** que usan
   *  el mapa y el picker, así que no agrega una petición. */
  private async _cargarLotesAsync(): Promise<void> {
    const finca = serviceProvider.getFincaSelectedService().getRequired();
    try {
      const lotes = await fx_createLoteGrpcService(finca.id).getListAsync();
      if (this._disposed) return;
      this._lotesFinca = new Map(lotes.map((lote) => [lote.id, lote]));
      // Las filas precargadas (edición) ya estaban pintadas sin área: recién
      // ahora se les puede calcular la columna de la derecha y los KPI.
      this.refresh();
    } catch {
      // Sin áreas, la tabla sigue sirviendo para anotar cantidades: lo único que
      // queda en blanco es la columna calculada. No vale romper el formulario.
    }
  }

  /** Las filas del request, con el área resuelta, en la forma que consume el
   *  cálculo. */
  private _getLotesMedidos(): LoteMedido[] {
    return this._request.lotes.map((lote) => ({
      loteId: lote.loteId,
      loteNombre: lote.loteNombre,
      areaHectareas: this._lotesFinca.get(lote.loteId)?.areaHectareas ?? 0,
      cantidad: lote.cantidad,
      plantas: lote.plantas,
    }));
  }

  /** Encabezados de las dos columnas editables/calculadas, según el método. */
  private _refreshEncabezados(): void {
    const esDrench = this._getParametros().metodo === "drench";
    if (this._encabezadoCantidad) {
      this._encabezadoCantidad.textContent = esDrench ? "Plantas" : "Recipientes";
    }
    if (this._encabezadoCalculado) {
      this._encabezadoCalculado.textContent = esDrench ? "Mezcla" : "L/ha";
    }
  }

  /** Re-pinta la tabla desde `request.lotes` (fuente de verdad). */
  private _refreshRows(): void {
    if (!this._rows) return;

    const parametros = this._getParametros();
    const esDrench = parametros.metodo === "drench";

    this._rows.innerHTML = this._getLotesMedidos()
      .map((lote, index) => {
        const valor = esDrench ? lote.plantas : lote.cantidad;
        const calculado = esDrench
          ? `${fx_num(getMezclaLitros(lote, parametros))} L`
          : fx_num(getDescargaLitrosHa(lote, parametros), 0);
        return mustache.render(rowTemplate, {
          index,
          nombre: lote.loteNombre,
          // El área del lote al lado del nombre: es lo que explica por qué dos
          // lotes con las mismas canecas tienen distinta descarga.
          area: lote.areaHectareas > 0 ? `${fx_num(lote.areaHectareas, 2)} ha` : "",
          valor: valor && valor > 0 ? valor : "",
          // Entero en plantas (media planta no existe), decimal en recipientes.
          paso: esDrench ? "1" : "0.5",
          calculado: lote.areaHectareas > 0 || esDrench ? calculado : "—",
        });
      })
      .join("");
  }

  /** Los cuatro totales de la cabecera. */
  private _refreshKpis(): void {
    if (!this._kpis) return;

    const parametros = this._getParametros();
    const esDrench = parametros.metodo === "drench";
    const resumen = getResumen(this._getLotesMedidos(), parametros);

    const kpis = [
      esDrench
        ? { etiqueta: "Plantas", valor: fx_num(resumen.plantas, 0), unidad: "" }
        : { etiqueta: "Recipientes", valor: fx_num(resumen.recipientes), unidad: "" },
      { etiqueta: "Mezcla", valor: fx_num(resumen.mezclaLitros), unidad: "L" },
      { etiqueta: "Área", valor: fx_num(resumen.areaHectareas, 2), unidad: "ha" },
      {
        etiqueta: "Descarga prom.",
        valor: fx_num(resumen.descargaLitrosHa, 0),
        unidad: "L/ha",
      },
    ];

    this._kpis.innerHTML = kpis
      .map(
        (kpi) =>
          `<div class="ap-kpi"><span>${kpi.etiqueta}</span><b>${kpi.valor}</b> <small>${kpi.unidad}</small></div>`,
      )
      .join("");
  }

  /** Redibuja el gráfico con los lotes y los productos actuales.
   *
   *  La dosis que se le pasa es **por recipiente**, que es la unidad en la que
   *  se dosifica en los métodos manuales — los únicos donde esta card existe. */
  private _refreshChart(): void {
    const lotes = this._getLotesMedidos();
    const productos = this._getProductos();
    const parametros = this._getParametros();

    this._chart.refresh(lotes, productos, parametros);
    this._refreshLeyenda(lotes, productos, parametros);
  }

  /** Productos con dosis cargada, en el orden en que se agregaron. Los de dosis
   *  cero quedan fuera: no aportan una barra ni un total. */
  private _getProductos(): ProductoDosis[] {
    return this._request.detalles
      .filter((detalle) => detalle.dosis > 0)
      .map((detalle) => ({
        nombre: detalle.productoDescripcion,
        dosisMlPorRecipiente: detalle.dosis,
      }));
  }

  /**
   * Leyenda del gráfico: un renglón por producto con su color y **el total de
   * litros en toda la aplicación**.
   *
   * Se arma acá y no se usa la del gráfico porque esa da el nombre del producto
   * y nada más. El total es lo que se lleva a la bodega —cuánto producto
   * comercial se gastó—, y es la única cifra del formulario que no se puede
   * sacar de los KPI: ellos cuentan mezcla, que es producto **más agua**.
   */
  private _refreshLeyenda(
    lotes: readonly LoteMedido[],
    productos: readonly ProductoDosis[],
    parametros: ParametrosMedicion,
  ): void {
    if (!this._leyenda) return;

    this._leyenda.innerHTML = productos
      .map((producto, indice) => {
        const litros = lotes.reduce(
          (suma, lote) =>
            suma + getLitrosProducto(lote, parametros, producto.dosisMlPorRecipiente),
          0,
        );
        return `<div class="ap-leyenda-item">
          <i style="background:${fx_colorProducto(indice)}"></i>
          <span class="ap-leyenda-nombre">${producto.nombre}</span>
          <em>${fx_num(litros, 2)} L</em>
        </div>`;
      })
      .join("");
  }

  /** Abre el picker con mapa, filtrando los lotes ya agregados. */
  private _onAgregarClick = (): void => {
    const yaAgregados = new Set(this._request.lotes.map((lote) => lote.loteId));
    this._picker = new LotePickerModalRender(
      "Lote aplicado",
      (lote) => this._onLoteElegido(lote),
      undefined,
      (lote) => !yaAgregados.has(lote.id),
    );
    this._picker.init();
  };

  /** Suma el lote elegido como fila. */
  private _onLoteElegido(lote: ClienteFincaLoteResponse): void {
    this._picker = null;
    if (this._disposed) return;

    // El picker ya trae el lote entero: se guarda acá por si el listado de la
    // finca todavía no terminó de cargar.
    this._lotesFinca.set(lote.id, lote);
    this._request.lotes.push({
      loteId: lote.id,
      loteNombre: lote.nombreLote,
      cantidad: null,
      plantas: null,
    });
    this.refresh();
    this.showError();
  }

  /** Delegado: escribe la cantidad editada en su lote. */
  private _onCantidadInput = (event: Event): void => {
    const input = event.target as HTMLInputElement | null;
    const index = Number(input?.dataset.cantidad ?? "");
    if (!input || isNaN(index) || !this._request.lotes[index]) return;

    const valor = parseFloat(input.value.replace(",", "."));
    const lote = this._request.lotes[index];
    if (this._getParametros().metodo === "drench") {
      // Plantas enteras: media planta no existe.
      lote.plantas = isNaN(valor) ? null : Math.round(valor);
      lote.cantidad = null;
    } else {
      lote.cantidad = isNaN(valor) ? null : valor;
      lote.plantas = null;
    }

    // Se repintan la columna calculada y los KPI, pero NO las filas: eso
    // reemplazaría el input que se está tecleando y se perdería el foco.
    this._refreshCalculado(index);
    this._refreshKpis();
    this._refreshChart();
    this.showError();
  };

  /** Actualiza solo la celda calculada de una fila, sin tocar su input. */
  private _refreshCalculado(index: number): void {
    const celda = this._rows?.querySelector<HTMLElement>(
      `[data-calculado="${index}"]`,
    );
    if (!celda) return;

    const parametros = this._getParametros();
    const lote = this._getLotesMedidos()[index];
    if (!lote) return;

    if (parametros.metodo === "drench") {
      celda.textContent = `${fx_num(getMezclaLitros(lote, parametros))} L`;
      return;
    }
    celda.textContent =
      lote.areaHectareas > 0
        ? fx_num(getDescargaLitrosHa(lote, parametros), 0)
        : "—";
  }

  /** Delegado: quita la fila del request. */
  private _onRemoveClick = (event: Event): void => {
    const boton = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-remove]",
    );
    if (!boton) return;
    const index = Number(boton.dataset.remove);
    if (isNaN(index)) return;

    this._request.lotes.splice(index, 1);
    this.refresh();
    this.showError();
  };

  private _setDisabled(disabled: boolean): void {
    this._rows
      ?.querySelectorAll<HTMLInputElement | HTMLButtonElement>("input, button")
      .forEach((el) => {
        el.disabled = disabled;
      });
    if (this._agregar instanceof HTMLButtonElement) {
      this._agregar.disabled = disabled;
    }
  }
}
